import { BiLeftArrowAlt, BiRightArrowAlt } from 'react-icons/bi';
import {
  Box,
  Button,
  Center,
  Flex,
  Icon,
  IconButton,
  Image,
  Text,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import AssignmentDataCard from '@/client/components/learners/dashboard/components/cards/AssignmentDataCard';
import AssignmentDataCardSkeleton from '@/client/components/learners/dashboard/components/cards/AssignmentDataCardSkeleton';
import NoAssignmentsPlaceholder from './NoAssignmentsPlaceholder';
import { motion } from 'framer-motion';
import { useGetDashboardUserAssignments } from '@/client/components/learners/dashboard/hooks/useGetUserAssignments';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function AssignmentCardSlider() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetDashboardUserAssignments();
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const isMobile = useBreakpointValue({ base: true, md: false });
  const isTablet = useBreakpointValue({ base: false, md: true, xl: false });

  // These are the breakpoints which changes the position of the
  // buttons as the screen size changes
  const top = useBreakpointValue({ base: '50%', md: '50%' });
  const side = useBreakpointValue({ base: '5%', md: '10px' });

  // Calculate how many cards to show based on viewport
  const getCardsToShow = () => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    return 4; // Desktop
  };

  const [cardsToShow, setCardsToShow] = useState(getCardsToShow());

  // Update container width when component mounts and on resize
  useLayoutEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateContainerWidth();
    window.addEventListener('resize', updateContainerWidth);
    return () => window.removeEventListener('resize', updateContainerWidth);
  }, []);

  // Update cards to show when viewport changes
  useEffect(() => {
    const handleResize = () => {
      setCardsToShow(getCardsToShow());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile, isTablet]);

  // Calculate max index to ensure the "View All" card is positioned at the right edge
  const calculateMaxIndex = () => {
    if (isMobile) {
      return Math.max(0, 7); // Show all cards on mobile
    }

    // For tablet and desktop, we want the "View All" card to be fully visible at the right edge
    // We need to scroll one position further to ensure the "View All" card is fully visible
    const visibleCards = isTablet ? 2 : 4;
    return Math.max(0, 8 - visibleCards + 1);
  };

  const maxIndex = calculateMaxIndex();

  const goToNext = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  // Animation variants for fade-in
  const fadeInVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  if (isLoading) {
    return (
      <Box display="flex" flexDirection="row" gap={4} width="full" paddingY={2}>
        {[...Array(4)].map((_, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <AssignmentDataCardSkeleton key={`skeleton-card-${idx}`} />
        ))}
      </Box>
    );
  }

  if (isError) {
    return (
      <motion.div initial="hidden" animate="visible" variants={fadeInVariants}>
        <div>Error...</div>
      </motion.div>
    );
  }

  const { openAssignments } = data;

  const sortAssignments = (assignments: typeof openAssignments) =>
    [...assignments].sort((a, b) => {
      // Handle cases where either assignment has no due date
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;

      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    });

  const sortedOpenAssignments = sortAssignments(openAssignments);

  if (sortedOpenAssignments.length === 0) {
    return <NoAssignmentsPlaceholder renderLink />;
  }

  return (
    <motion.div initial="hidden" animate="visible" variants={fadeInVariants}>
      <Box position="relative" height="fit-content" width="full" overflow="hidden">
        {/* Navigation Buttons */}
        {currentIndex !== 0 && (
          <IconButton
            isDisabled={currentIndex === 0}
            aria-label="left-arrow"
            borderRadius="full"
            position="absolute"
            left={side}
            top={top}
            transform="translate(0%, -50%)"
            zIndex={2}
            backgroundColor="white"
            color="neutral.1000"
            border="2px solid"
            borderColor="neutral.100"
            boxShadow="0px 2px 6px 1px rgba(0, 0, 0, 0.10)"
            _hover={{
              backgroundColor: 'neutral.50',
            }}
            _disabled={{
              color: 'neutral.200',
            }}
            onClick={goToPrevious}
          >
            <Icon as={BiLeftArrowAlt} boxSize={5} />
          </IconButton>
        )}

        {currentIndex !== maxIndex && (
          <IconButton
            isDisabled={currentIndex >= maxIndex}
            aria-label="right-arrow"
            borderRadius="full"
            position="absolute"
            right={side}
            top={top}
            transform="translate(0%, -50%)"
            zIndex={2}
            backgroundColor="white"
            color="neutral.1000"
            border="2px solid"
            borderColor="neutral.100"
            boxShadow="0px 2px 6px 1px rgba(0, 0, 0, 0.10)"
            _hover={{
              backgroundColor: 'neutral.50',
            }}
            _disabled={{
              color: 'neutral.200',
            }}
            onClick={goToNext}
          >
            <Icon as={BiRightArrowAlt} boxSize={5} />
          </IconButton>
        )}

        {/* Slider Container */}
        <Box
          ref={containerRef}
          overflow="hidden"
          width="full"
          paddingY={isMobile ? 2 : 6}
          paddingX={isMobile ? 0 : 4}
        >
          <Flex
            gap={4}
            transition="transform 0.3s ease-in-out"
            transform={`translateX(-${
              currentIndex *
              (isMobile
                ? containerWidth > 0
                  ? containerWidth + 16
                  : window.innerWidth - 16
                : isTablet
                ? 300 + 16
                : 300 + 16)
            }px)`}
            width="fit-content"
          >
            {sortedOpenAssignments.slice(0, 7).map((assignment) => (
              <Box
                key={assignment._id}
                flexShrink={0}
                width={
                  isMobile
                    ? containerWidth > 0
                      ? `${containerWidth}px`
                      : `${window.innerWidth - 32}px`
                    : isTablet
                    ? '300px'
                    : '300px'
                }
                minWidth={
                  isMobile
                    ? containerWidth > 0
                      ? `${containerWidth}px`
                      : `${window.innerWidth - 32}px`
                    : isTablet
                    ? '300px'
                    : '300px'
                }
              >
                <AssignmentDataCard assignment={assignment} />
              </Box>
            ))}

            <Box
              key="view-more-placeholder"
              flexShrink={0}
              width={
                isMobile
                  ? containerWidth > 0
                    ? `${containerWidth}px`
                    : `${window.innerWidth - 32}px`
                  : isTablet
                  ? '300px'
                  : '300px'
              }
              minWidth={
                isMobile
                  ? containerWidth > 0
                    ? `${containerWidth}px`
                    : `${window.innerWidth - 32}px`
                  : isTablet
                  ? '300px'
                  : '300px'
              }
              border="1px solid"
              borderColor="neutral.100"
              borderRadius="16px"
              transition="box-shadow 400ms"
              _hover={{ cursor: 'pointer', boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, .1)' }}
              onClick={(e) => {
                e.stopPropagation();

                navigate('assignments');
              }}
            >
              <VStack spacing={2} paddingY={5}>
                <Image
                  src="/public/images/Pencil-Writing-Streamline-UX-Colors.svg"
                  alt={`${t('learners.dashboard.overview.viewAll')}`}
                />

                <VStack spacing={4}>
                  <Text fontSize="14px" fontWeight={600} color="baseBlack">
                    {sortedOpenAssignments.length > 7
                      ? t('learners.dashboard.overview.moreAssignmentsCount', {
                          count: Math.max(0, sortedOpenAssignments.length - 7),
                        })
                      : t('learners.dashboard.overview.noAdditionalAssignments')}
                  </Text>

                  <Button
                    size="sm"
                    paddingX={3}
                    paddingY={2}
                    backgroundColor="neutral.50"
                    color="neutral.1200"
                    borderRadius="8px"
                    _hover={{
                      backgroundColor: 'neutral.50',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();

                      navigate('assignments');
                    }}
                  >
                    {t('learners.dashboard.overview.viewAll')}
                  </Button>
                </VStack>
              </VStack>
            </Box>
          </Flex>
        </Box>
      </Box>
    </motion.div>
  );
}
